import { t } from "i18next";
import capitalize from "lodash/capitalize";
import { translateAction, translateTrigger } from "../locales/i18n";
import {
  findInputAndPick,
  pickActionName,
  pickValueConfigString,
} from "./inputs";
import type { ActionCandidate, TriggerCandidate } from "../types/candidate";
import { INPUT_APPLICATION_ID } from "./constants";
import { actionSolutionId } from "./action-solutions";
import { actionApplicationId } from "./record-list-identity";
import { solutionMemberScope } from "./member-scope";
import { useRootStore } from "../stores/hooks";
import type { ContentItem, Doc } from "@smartsuite/smartdoc";
import { updateNestedSmartDocInLinkMarks } from "./smartdoc-nested-link-href";
import type {
  ActionConfig,
  OutputReference,
  TriggerConfig,
} from "@smartsuite/automation-engine-api";
import type { LoopingStore } from "../stores/automation/looping.store";

interface PillTooltip {
  title: string;
  text: string | undefined;
}

type LoopingSourceContext = Pick<LoopingStore, "source">;

interface PillTooltipFunctions {
  makeTriggerPillTooltip: (trigger: TriggerConfig) => PillTooltip;
  makeActionPillTooltip: (action: ActionConfig) => PillTooltip;
  makeCurrentItemPillTooltip: (
    looping: LoopingSourceContext,
  ) => PillTooltip | undefined;
  addTooltipToPills: (
    smartdoc: Doc,
    trigger: TriggerCandidate,
    actions: ActionCandidate[],
    currentAction?: ActionCandidate,
  ) => Doc;
}

export const usePillTooltip = (): PillTooltipFunctions => {
  const store = useRootStore();
  const { applications, automations } = store;

  const addTooltipToPills = (
    smartdoc: Doc,
    trigger: TriggerCandidate,
    actions: ActionCandidate[],
    currentAction?: ActionCandidate,
  ): Doc => {
    // A team pill is valid only if the team has access to the solution the
    // owning action writes into -- the same scope its picker offered.
    const scopedTeams = solutionMemberScope(
      store,
      actionSolutionId(currentAction, store.config.solutionId),
    ).teams;
    const checkPillContent = (content: ContentItem): ContentItem => {
      if (content.type === "pill") {
        let value: OutputReference;
        try {
          // Sometimes users create pill with value that is not valid JSON
          value = JSON.parse(String(content.attrs?.value));
          // team pill from teams tab has no source
          if (value.source === undefined) {
            // @ts-expect-error - team pill carries teamId in attrs.value
            const teamId = value.teamId;
            if (teamId !== undefined && content.attrs !== undefined) {
              content.attrs.invalid = !scopedTeams.some(
                (team) => team.teamId === teamId,
              );
            }
            return content;
          }

          if (
            value.source.oneofKind === "triggerReference" &&
            content.attrs !== undefined
          ) {
            // @ts-expect-error
            content.attrs.tooltip = makeTriggerPillTooltip(trigger);
          }

          if (
            value.source.oneofKind === "actionReference" &&
            actions.length > 0
          ) {
            const instanceId = value.source.actionReference.instanceId;
            const actionToUse = actions.find(
              (item) => item.actionReference?.instanceId === instanceId,
            );
            if (actionToUse) {
              // @ts-expect-error
              content.attrs.tooltip = makeActionPillTooltip(actionToUse);
            }
          }
          return content;
        } catch (_) {
          // Deliberately catching and handling JSON parse errors without logging
          // to gracefully handle invalid pill values and mark them as invalid
          if (content.attrs !== undefined) {
            content.attrs.invalid = true;
          }
          return content;
        }
      }
      if (content.marks !== undefined && content.marks.length > 0) {
        return updateNestedSmartDocInLinkMarks(content, checkPillContent);
      }

      return content;
    };
    const contentItems = smartdoc.content.map((content: ContentItem) => {
      const innerContent = (content.content ?? []).map((c: ContentItem) => {
        return checkPillContent(c);
      });

      return {
        ...content,
        content: innerContent,
      };
    });

    return {
      ...smartdoc,
      content: contentItems,
    };
  };

  const makeTriggerPillTooltip = (
    trigger: TriggerConfig,
  ): { title: string; text: string | undefined } => {
    let applicationName: string | undefined;
    const selectedApp = findInputAndPick(
      trigger.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );
    if (selectedApp) {
      applicationName =
        applications.list.find((app) => app.id === selectedApp)?.label ?? "";
    }
    return {
      title: `${capitalize(t("general.trigger.one"))}: ${translateTrigger({
        triggerId: trigger.triggerReference?.triggerId,
        label: "",
      })}`,
      text: applicationName === undefined ? undefined : `In ${applicationName}`,
    };
  };

  const makeActionPillTooltip = (
    action: ActionConfig,
  ): { title: string; text: string | undefined } => {
    let applicationName: string | undefined;

    const selectedApp = actionApplicationId(action);
    if (selectedApp) {
      applicationName =
        applications.list.find((app) => app.id === selectedApp)?.label ?? "";
    }

    if (applicationName === undefined || applicationName === "") {
      // lets try to find app name in actions description
      automations.candidate.self.actions.find((action) => {
        const inputDescription = (
          action as ActionCandidate
        ).description.inputs.find(
          (input) => input.inputId === "application-id",
        );
        if (inputDescription !== undefined) {
          if (inputDescription.editor?.editor.oneofKind === "select") {
            const option = inputDescription.editor?.editor.select.options.find(
              (option) => option.value === selectedApp,
            );
            if (option !== undefined) {
              applicationName = option.label;
              return true;
            }
          }
        }
        return false;
      });
    }

    let actionName = pickActionName(
      action.inputs,
      action.actionReference?.actionId,
    );
    actionName ??= translateAction({
      actionId: action.actionReference?.actionId,
      label: "",
    });

    return {
      title: `${capitalize(t("general.action.one"))}: ${actionName}`,
      text:
        applicationName === undefined || applicationName === ""
          ? undefined
          : `In ${applicationName}`,
    };
  };

  /**
   * Tooltip for a "Current item" pill produced by Loop by Field.
   * The current item resolves to the loop source (a trigger or action),
   * so its tooltip mirrors that source's tooltip regardless of field type.
   */
  const makeCurrentItemPillTooltip = (
    looping: LoopingSourceContext,
  ): PillTooltip | undefined => {
    const source = looping.source;
    if (source?.oneofKind !== "outputReference") {
      return undefined;
    }

    const referenceSource = source.outputReference.source;
    if (referenceSource.oneofKind === "triggerReference") {
      return makeTriggerPillTooltip(automations.candidate.self.trigger);
    }
    if (referenceSource.oneofKind === "actionReference") {
      const { instanceId } = referenceSource.actionReference;
      const action = automations.candidate.allActions.find(
        (item) => item.actionReference?.instanceId === instanceId,
      );
      return action === undefined ? undefined : makeActionPillTooltip(action);
    }

    return undefined;
  };

  return {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
    addTooltipToPills,
  };
};
